import { Box, Typography } from '@mui/material';
import {
  Fact,
  LedgerHoverCard,
  Money,
  RefCode,
  StatusWord,
  formatLedgerWhen,
  formatLedgerWhenFull,
  statusToken,
} from '../../../shared/components/ledger';
import type { PotLedgerEntry } from './toPotLedgerRows';

/**
 * The pot breakdown's fact list. Facts are conditional — the card shows what
 * the row DTO carries and nothing else.
 *
 * G2: `AllocationDetailV2` carries no labour item value and no partial-booking
 * flag, so Target and Booked % are omitted here. They come back when the pot
 * projection exposes them.
 */

export interface PanelLink {
  label: string;
  onOpen: () => void;
}

export interface PotLedgerHoverCardProps {
  entry: PotLedgerEntry;
  onOpenDetails: () => void;
  /** Extra side panels reachable from the row: site, plot, labour item. */
  links: readonly PanelLink[];
}

function kindOf(entry: PotLedgerEntry): string {
  switch (entry.kind) {
    case 'deduction':
      return 'Deduction';
    case 'variation':
      return 'Variation';
    default:
      return 'Labour item';
  }
}

interface Reviewer {
  name: string;
  at: string | null;
}

/** One field serves both roles on a deduction; work rows keep them apart. */
function approver(entry: PotLedgerEntry): Reviewer | null {
  if (entry.kind === 'deduction') {
    return entry.reviewedByName
      ? { name: entry.reviewedByName, at: entry.reviewedAt }
      : null;
  }
  return entry.approvedByName
    ? { name: entry.approvedByName, at: entry.approvedAt }
    : null;
}

function sitePlot(entry: PotLedgerEntry): string | null {
  const parts = [entry.siteName, entry.plotReference].filter(
    (part): part is string => (part ?? '').trim().length > 0
  );
  return parts.length > 0 ? parts.join(' · ') : null;
}

const linkSx = {
  p: 0,
  border: 0,
  bgcolor: 'transparent',
  color: 'primary.main',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 'inherit',
  '&:hover': { textDecoration: 'underline' },
} as const;

export function PotLedgerHoverCard({
  entry,
  onOpenDetails,
  links,
}: PotLedgerHoverCardProps) {
  const approval = approver(entry);
  const where = sitePlot(entry);
  const rejectedBy = entry.kind === 'deduction' ? null : entry.rejectedByName;
  const rejectionReason =
    entry.kind === 'deduction' ? null : entry.rejectionReason;

  return (
    <LedgerHoverCard
      title={entry.title}
      kind={kindOf(entry)}
      description={
        entry.kind === 'variation'
          ? (entry.description ?? undefined)
          : undefined
      }
      onOpenDetails={onOpenDetails}
    >
      <Fact label="Status">
        <StatusWord token={statusToken(entry.status)} />
      </Fact>
      {approval && (
        <Fact
          label={entry.kind === 'deduction' ? 'Reviewed by' : 'Approved by'}
        >
          {approval.at
            ? `${approval.name} · ${formatLedgerWhen(approval.at)}`
            : approval.name}
        </Fact>
      )}
      {rejectedBy && <Fact label="Rejected by">{rejectedBy}</Fact>}
      {rejectionReason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', py: 0.25 }}
        >
          “{rejectionReason}”
        </Typography>
      )}
      {entry.reference && (
        <Fact label="Ref">
          <RefCode>{entry.reference}</RefCode>
        </Fact>
      )}
      {where && <Fact label="Site · Plot">{where}</Fact>}
      {entry.who && (
        <Fact label={entry.kind === 'deduction' ? 'Added by' : 'Booked by'}>
          {entry.who}
        </Fact>
      )}
      <Fact label="When">{formatLedgerWhenFull(entry.when)}</Fact>
      <Fact label="Value">
        <Money
          value={entry.amount}
          color={entry.kind === 'deduction' ? 'error.main' : undefined}
        />
      </Fact>
      {links.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.75 }}>
          {links.map(link => (
            <Typography
              key={link.label}
              component="button"
              variant="caption"
              onClick={link.onOpen}
              sx={linkSx}
            >
              {link.label}
            </Typography>
          ))}
        </Box>
      )}
    </LedgerHoverCard>
  );
}
